import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

const STATUS_COLORS = {
  pending:   { bg: '#fef9c3', color: '#854d0e', label: '⏳ Pending' },
  confirmed: { bg: '#dbeafe', color: '#1e40af', label: '✅ Confirmed' },
  preparing: { bg: '#fde68a', color: '#92400e', label: '👨‍🍳 Preparing' },
  ready:     { bg: '#d1fae5', color: '#065f46', label: '🔔 Ready' },
  completed: { bg: '#f0fdf4', color: '#166534', label: '✔️ Completed' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', label: '❌ Cancelled' },
};

// ── Sound Player — robust AudioContext with explicit unlock ───────────
let _audioCtx = null;
let _audioUnlocked = false;

async function unlockAudio() {
  try {
    if (!_audioCtx) {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (_audioCtx.state === 'suspended') {
      await _audioCtx.resume();
    }
    _audioUnlocked = _audioCtx.state === 'running';
    return _audioUnlocked;
  } catch (e) {
    console.warn('Audio unlock failed:', e);
    return false;
  }
}

// Request browser notification permission
async function requestNotificationPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

// Show browser notification (works in background tabs)
function showOrderNotification(count) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const n = new Notification('🔔 New Order — QR Digi Menu', {
    body: `${count} new order${count > 1 ? 's' : ''} waiting for confirmation!`,
    icon: '/logo.png',
    badge: '/logo.png',
    tag: 'new-order', // replaces previous notification instead of stacking
    requireInteraction: true, // stays on screen until dismissed
  });
  n.onclick = () => { window.focus(); n.close(); };
}

// BroadcastChannel — syncs order alerts across all open tabs
let _orderChannel = null;
function getOrderChannel() {
  if (!_orderChannel && typeof BroadcastChannel !== 'undefined') {
    _orderChannel = new BroadcastChannel('qrdm_orders');
  }
  return _orderChannel;
}

// Try passive unlock on any user interaction
if (typeof window !== 'undefined') {
  const passiveUnlock = () => { unlockAudio(); };
  ['click','touchstart','keydown','pointerdown'].forEach(evt =>
    document.addEventListener(evt, passiveUnlock, { once: false, passive: true })
  );
}

async function playNotificationSound() {
  try {
    const unlocked = await unlockAudio();
    if (!unlocked) {
      console.warn('AudioContext not unlocked yet — waiting for user gesture');
      return;
    }
    const ctx = _audioCtx;
    const playBeep = (startTime, freq, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.6, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    playBeep(ctx.currentTime,        880,  0.20);
    playBeep(ctx.currentTime + 0.25, 1046, 0.20);
    playBeep(ctx.currentTime + 0.50, 1318, 0.25);
  } catch (e) {
    console.warn('Sound error:', e);
  }
}

// ── Confirmation Time Popup ──────────────────────────────────────────
function ConfirmTimePopup({ order, onConfirm, onCancel }) {
  const [prepTime, setPrepTime] = useState(20);
  const [sending, setSending] = useState(false);

  const handleConfirm = async () => {
    setSending(true);
    await onConfirm(order.order_id, prepTime);
    setSending(false);
  };

  const timeOptions = [10, 15, 20, 25, 30, 40, 45, 60, 90];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'var(--primary)', padding: '20px 24px', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Confirm Order #{order.order_number}</h2>
          <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: 14 }}>Customer: <strong>{order.customer_name}</strong></p>
        </div>

        <div style={{ padding: '24px' }}>
          <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>⏱️ How long will the order take?</p>

          {/* Quick Time Buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, justifyContent: 'center' }}>
            {timeOptions.map(t => (
              <button key={t} onClick={() => setPrepTime(t)} style={{
                padding: '8px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', border: '2px solid',
                background: prepTime === t ? 'var(--primary)' : 'white',
                color: prepTime === t ? 'white' : 'var(--primary)',
                borderColor: 'var(--primary)', transition: 'all 0.15s',
              }}>
                {t} min
              </button>
            ))}
          </div>

          {/* Custom Time Input */}
          <div style={{ marginBottom: 20, textAlign: 'center' }}>
            <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 6 }}>Or enter custom time:</label>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <input
                type="number" min="1" max="180" value={prepTime}
                onChange={e => setPrepTime(parseInt(e.target.value) || 20)}
                style={{ width: 80, textAlign: 'center', padding: '8px', border: '2px solid var(--primary)', borderRadius: 10, fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}
              />
              <span style={{ fontSize: 15, fontWeight: 600 }}>minutes</span>
            </div>
          </div>

          {/* Email notice */}
          {order.customer_email && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>📧</span>
              <span>Auto email will be sent to <strong>{order.customer_email}</strong> with {prepTime} min preparation time</span>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onCancel} style={{ flex: 1, padding: '12px', border: '1px solid var(--border)', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', background: 'white', color: '#555' }}>
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={sending} style={{ flex: 2, padding: '12px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
              {sending ? 'Confirming...' : `✅ Confirm & Send Email`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────
export default function OnlineOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [orderTypeTab, setOrderTypeTab] = useState('online');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [confirmingOrder, setConfirmingOrder] = useState(null);
  const [newOrderIds, setNewOrderIds] = useState(new Set()); // tracks new pending orders
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundReady, setSoundReady] = useState(
    localStorage.getItem('qrdm_sound_enabled') === 'true'
  ); // persists across sessions

  const toast = useToast();
  const restaurantRef = useRef(null);
  const knownOrderIdsRef = useRef(null); // null = first load
  const soundIntervalRef = useRef(null);
  const pendingCountRef = useRef(0);

  // ── Stop sound interval ────────────────────────────────────────────
  const stopSound = useCallback(() => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
  }, []);

  // ── Start repeating sound for pending orders ───────────────────────
  const startSound = useCallback(() => {
    stopSound();
    if (!soundEnabled || !soundReady) return;
    unlockAudio().then(unlocked => {
      if (!unlocked) return;
      playNotificationSound();
      soundIntervalRef.current = setInterval(() => {
        if (pendingCountRef.current > 0 && soundEnabled) {
          playNotificationSound();
        } else {
          stopSound();
        }
      }, 4000);
    });
  }, [soundEnabled, soundReady, stopSound]);

  // ── Load orders + detect new ones ─────────────────────────────────
  const loadOrders = useCallback((rid, isAutoRefresh = false) => {
    if (!isAutoRefresh) setLoading(true);
    api.get(`/restaurants/${rid}/orders`)
      .then(data => {
        setOrders(data);

        // Count pending
        const pendingOrders = data.filter(o => o.status === 'pending');
        pendingCountRef.current = pendingOrders.length;

        if (knownOrderIdsRef.current === null) {
          // First load — just record existing IDs, no alert
          knownOrderIdsRef.current = new Set(data.map(o => o.order_id));
        } else {
          // Subsequent loads — find truly new orders
          const incoming = data.filter(o => !knownOrderIdsRef.current.has(o.order_id));
          if (incoming.length > 0) {
            const newIds = new Set(incoming.map(o => o.order_id));
            setNewOrderIds(prev => new Set([...prev, ...newIds]));
            incoming.forEach(o => knownOrderIdsRef.current.add(o.order_id));

            // Play sound + notification for new pending orders
            const newPending = incoming.filter(o => o.status === 'pending');
            if (newPending.length > 0) {
              startSound();
              toast.success(`🔔 ${newPending.length} new order${newPending.length > 1 ? 's' : ''} received!`);
              // Show browser notification (works in background/other tabs)
              showOrderNotification(newPending.length);
              // Broadcast to other open tabs
              const ch = getOrderChannel();
              if (ch) ch.postMessage({ type: 'NEW_ORDER', count: newPending.length });
            }
          }

          // Stop sound if no more pending
          if (pendingOrders.length === 0) stopSound();
        }
      })
      .catch(() => setOrders([]))
      .finally(() => { if (!isAutoRefresh) setLoading(false); });
  }, [startSound, stopSound, toast]);

  // ── Auto-unlock AudioContext on mount if previously enabled ──────────
  useEffect(() => {
    if (localStorage.getItem('qrdm_sound_enabled') === 'true') {
      // Try to auto-unlock — may fail silently if no gesture yet, that's OK
      unlockAudio().catch(() => {});
    }
  }, []);

  // ── BroadcastChannel — receive new order alerts from other tabs ──────
  useEffect(() => {
    const ch = getOrderChannel();
    if (!ch) return;
    const handler = (event) => {
      if (event.data?.type === 'NEW_ORDER') {
        if (soundReady && soundEnabled) playNotificationSound();
        showOrderNotification(event.data.count || 1);
        if (restaurantRef.current) {
          loadOrders(restaurantRef.current.restaurant_id, true);
        }
      }
    };
    ch.addEventListener('message', handler);
    return () => ch.removeEventListener('message', handler);
  }, [soundReady, soundEnabled, loadOrders]);

  // ── Initial load ───────────────────────────────────────────────────
  useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) {
        setRestaurant(data[0]);
        restaurantRef.current = data[0];
        if ((data[0].plan || 'basic') === 'pro') {
          loadOrders(data[0].restaurant_id, false);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }).catch(() => setLoading(false));
  }, [loadOrders]);

  // ── Auto-refresh every 15 seconds ─────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (restaurantRef.current) {
        loadOrders(restaurantRef.current.restaurant_id, true);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  // Keep restaurantRef in sync
  useEffect(() => { restaurantRef.current = restaurant; }, [restaurant]);

  // Cleanup sound on unmount
  useEffect(() => () => stopSound(), [stopSound]);

  // ── Dismiss new order highlight ────────────────────────────────────
  const dismissNew = (orderId) => {
    setNewOrderIds(prev => { const s = new Set(prev); s.delete(orderId); return s; });
  };

  const handleConfirmWithTime = async (orderId, prepTime) => {
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/orders/${orderId}/confirm`, { prep_time: prepTime });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: 'confirmed', prep_time: prepTime } : o));
      setConfirmingOrder(null);
      dismissNew(orderId);
      toast.success(`Order confirmed! Email sent with ${prepTime} min prep time ✅`);
      // Check if any pending remain
      const remaining = orders.filter(o => o.order_id !== orderId && o.status === 'pending');
      if (remaining.length === 0) { pendingCountRef.current = 0; stopSound(); }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    if (newStatus === 'confirmed') {
      const order = orders.find(o => o.order_id === orderId);
      setConfirmingOrder(order);
      return;
    }
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/orders/${orderId}/status`, { status: newStatus });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o));
      dismissNew(orderId);
      toast.success(`Order status updated to ${newStatus}!`);
      if (newStatus === 'cancelled') {
        const remaining = orders.filter(o => o.order_id !== orderId && o.status === 'pending');
        if (remaining.length === 0) { pendingCountRef.current = 0; stopSound(); }
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const typeFiltered = orders.filter(o => (o.order_type || 'online') === orderTypeTab);
  const filtered = filterStatus === 'all' ? typeFiltered : typeFiltered.filter(o => o.status === filterStatus);
  const statusCounts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const pendingCount = statusCounts.pending || 0;

  if (loading) return <AdminLayout title="Orders"><div className="loading-center"><div className="loading-spinner"></div></div></AdminLayout>;

  // ── Basic Plan Upgrade Wall ─────────────────────────────────────────
  if (restaurant && (restaurant.plan || 'basic') === 'basic') {
    return (
      <AdminLayout title="Online Orders">
        <div style={{ maxWidth: 600, margin: '60px auto', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontSize: 72, marginBottom: 20 }}>🔒</div>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12, color: '#0f1117' }}>Online Orders is a Pro Feature</h2>
          <p style={{ fontSize: 16, color: '#666', lineHeight: 1.7, marginBottom: 32 }}>
            Upgrade to <strong>Pro</strong> to unlock online ordering, real-time sound notifications,
            automatic email confirmations to customers, and full order management.
          </p>
          <div style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 16, padding: '24px 32px', marginBottom: 32 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#d97706', letterSpacing: 1, marginBottom: 8 }}>PRO PLAN — $39.99/month</div>
            {[
              '🛒 Online ordering system',
              '🔔 Real-time sound notifications',
              '📧 Auto email confirmations to customers',
              '🍽️ Catering request management',
              '🌍 Multi-language menu',
              '📊 Advanced analytics dashboard',
            ].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #fde68a', fontSize: 15, color: '#333', textAlign: 'left' }}>
                <span style={{ color: '#d97706', fontWeight: 700 }}>✓</span> {f}
              </div>
            ))}
          </div>
          <a href="/admin/settings" style={{
            display: 'inline-block', background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: '#0f1117', padding: '14px 40px', borderRadius: 12,
            fontSize: 16, fontWeight: 800, textDecoration: 'none',
            boxShadow: '0 4px 16px rgba(245,158,11,0.4)',
          }}>
            ⭐ Upgrade to Pro in Settings
          </a>
          <p style={{ marginTop: 16, fontSize: 13, color: '#999' }}>
            Go to Settings → Select Pro Plan → Save Changes
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Online Orders">
      <div style={{ maxWidth: 900 }}>

        {/* ── One-time Sound Enable Prompt ── */}
        {!soundReady && (
          <div style={{
            background: 'linear-gradient(135deg, #1a5c2a, #145022)',
            borderRadius: 14, padding: '16px 20px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 28 }}>🔔</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'white', marginBottom: 3 }}>
                  Enable Sound Notifications
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
                  Click once to get automatic sound alerts for every new order
                </div>
              </div>
            </div>
            <button
              onClick={async () => {
                const unlocked = await unlockAudio();
                if (unlocked) {
                  setSoundReady(true);
                  localStorage.setItem('qrdm_sound_enabled', 'true');
                  playNotificationSound();
                  await requestNotificationPermission();
                }
              }}
              style={{
                background: '#4ade80', color: '#0f1117',
                border: 'none', borderRadius: 10,
                padding: '10px 22px', fontWeight: 800,
                fontSize: 14, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(74,222,128,0.4)',
                whiteSpace: 'nowrap',
              }}
            >
              🔔 Enable Sound — Click Once
            </button>
          </div>
        )}

        {/* 🔔 New Pending Orders Banner */}
        {pendingCount > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #ff6b35, #f7c59f)',
            borderRadius: 14, padding: '14px 20px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 4px 16px rgba(255,107,53,0.35)',
            animation: 'pulse 1.5s infinite',
            flexWrap: 'wrap', gap: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 28 }}>🔔</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'white' }}>
                  {pendingCount} Pending Order{pendingCount > 1 ? 's' : ''} Waiting!
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
                  Please confirm or cancel — sound will stop once handled
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* Test sound button — also unlocks AudioContext */}
              <button
                onClick={async () => { await unlockAudio(); playNotificationSound(); }}
                style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: 'white', fontWeight: 600, fontSize: 13 }}
              >
                🔔 Test Sound
              </button>
              <button
                onClick={async () => {
                  const next = !soundEnabled;
                  setSoundEnabled(next);
                  if (!next) {
                    stopSound();
                  } else {
                    const unlocked = await unlockAudio();
                    if (unlocked) { setSoundReady(true); playNotificationSound(); }
                  }
                }}
                style={{ background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: 'white', fontWeight: 700, fontSize: 13 }}
              >
                {soundEnabled ? '🔇 Mute' : '🔊 Unmute'}
              </button>
            </div>
          </div>
        )}

        {/* CSS for pulse animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% { box-shadow: 0 4px 16px rgba(255,107,53,0.35); }
            50% { box-shadow: 0 4px 28px rgba(255,107,53,0.65); }
          }
          @keyframes newOrderGlow {
            0%, 100% { border-color: #f97316; }
            50% { border-color: #fb923c; box-shadow: 0 0 12px rgba(249,115,22,0.4); }
          }
        `}</style>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Orders', value: typeFiltered.length, icon: '📋' },
            { label: 'Pending', value: pendingCount, icon: '⏳', highlight: pendingCount > 0 },
            { label: 'Preparing', value: statusCounts.preparing || 0, icon: '👨‍🍳' },
            { label: 'Completed', value: statusCounts.completed || 0, icon: '✔️' },
          ].map(stat => (
            <div key={stat.label} className="card" style={{
              padding: '16px', textAlign: 'center',
              border: stat.highlight ? '2px solid #f97316' : undefined,
              background: stat.highlight ? '#fff7ed' : undefined,
            }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{stat.icon}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: stat.highlight ? '#ea580c' : 'var(--primary)' }}>{stat.value}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Order Type Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', width: 'fit-content' }}>
          {[{ key: 'online', label: '📦 Online Orders' }, { key: 'dine_in', label: '🍽️ Dine-in Orders' }].map(t => (
            <button key={t.key} onClick={() => { setOrderTypeTab(t.key); setFilterStatus('all'); }} style={{
              padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              border: 'none', borderRight: '1px solid var(--border)',
              background: orderTypeTab === t.key ? 'var(--primary)' : 'white',
              color: orderTypeTab === t.key ? 'white' : '#555',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {['all', ...Object.keys(STATUS_COLORS)].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--border)',
              background: filterStatus === s ? 'var(--primary)' : 'white',
              color: filterStatus === s ? 'white' : '#555',
            }}>
              {s === 'all' ? `All (${orders.length})` : `${STATUS_COLORS[s]?.label} (${statusCounts[s] || 0})`}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <h3>{orders.length === 0 ? 'No orders yet' : 'No orders with this status'}</h3>
            <p>{orders.length === 0 ? 'Orders from customers will appear here' : 'Try a different filter'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(order => {
              const statusInfo = STATUS_COLORS[order.status] || STATUS_COLORS.pending;
              const isExpanded = expandedOrder === order.order_id;
              const isNew = newOrderIds.has(order.order_id);
              const isPending = order.status === 'pending';

              return (
                <div key={order.order_id} className="card" style={{
                  overflow: 'hidden',
                  border: isNew || isPending ? '2px solid #f97316' : undefined,
                  animation: isNew ? 'newOrderGlow 1.5s infinite' : undefined,
                }}>
                  {/* NEW badge */}
                  {isNew && (
                    <div style={{ background: '#f97316', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 12px', textAlign: 'center', letterSpacing: 1 }}>
                      🆕 NEW ORDER
                    </div>
                  )}

                  {/* Header */}
                  <div onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
                    style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, flex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span>Order #{order.order_number || order.order_id.slice(-4).toUpperCase()}</span>
                      <span style={{ background: statusInfo.bg, color: statusInfo.color, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                        {statusInfo.label}
                      </span>
                      {order.prep_time && order.status === 'confirmed' && (
                        <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>
                          ⏱️ {order.prep_time} min
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', marginRight: 12 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{order.customer_name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{order.customer_phone}</div>
                      {order.customer_email && <div style={{ fontSize: 11, color: '#aaa' }}>{order.customer_email}</div>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--primary)', minWidth: 70, textAlign: 'right' }}>
                      ${Number(order.total_amount).toFixed(2)}
                    </div>
                    <span style={{ fontSize: 18, color: '#999', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>▾</span>
                  </div>

                  {/* Quick action buttons for pending orders (always visible) */}
                  {isPending && !isExpanded && (
                    <div style={{ padding: '0 18px 14px', display: 'flex', gap: 8 }}>
                      <button onClick={() => updateStatus(order.order_id, 'confirmed')} style={{ flex: 1, padding: '8px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        ✅ Confirm
                      </button>
                      <button onClick={() => updateStatus(order.order_id, 'cancelled')} style={{ flex: 1, padding: '8px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        ❌ Cancel
                      </button>
                    </div>
                  )}

                  {/* Expanded */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px' }}>
                      {/* Customer Info */}
                      <div style={{ background: 'var(--primary-light)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                        <h4 style={{ fontWeight: 700, marginBottom: 8, fontSize: 14 }}>👤 Customer Info</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                          <div><span style={{ color: '#666' }}>Name: </span><strong>{order.customer_name}</strong></div>
                          <div><span style={{ color: '#666' }}>Phone: </span><strong>{order.customer_phone}</strong></div>
                          {order.table_number && <div><span style={{ color: '#666' }}>Table: </span><strong>{order.table_number}</strong></div>}
                        </div>
                      </div>

                      {/* Order Items */}
                      <div style={{ marginBottom: 16 }}>
                        <h4 style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>🧾 Order Items</h4>
                        {(order.items || []).map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                            <span>{item.quantity}× {item.name}{item.spice_choice ? ` (${item.spice_choice})` : ''}</span>
                            <span style={{ fontWeight: 600 }}>${Number(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontWeight: 700, fontSize: 15 }}>
                          <span>Total</span>
                          <span style={{ color: 'var(--primary)' }}>${Number(order.total_amount).toFixed(2)}</span>
                        </div>
                      </div>

                      {order.notes && (
                        <div style={{ marginBottom: 16, background: '#fef9c3', borderRadius: 8, padding: '10px 14px', fontSize: 14 }}>
                          📝 <strong>Note:</strong> {order.notes}
                        </div>
                      )}

                      <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                        🕐 Ordered: {new Date(order.created_at).toLocaleString()}
                        {order.prep_time && <span style={{ marginLeft: 12 }}>⏱️ Prep time: <strong>{order.prep_time} min</strong></span>}
                      </div>

                      {/* Status Actions */}
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Update Status:</p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {Object.entries(STATUS_COLORS).map(([status, info]) => (
                            <button key={status}
                              onClick={() => updateStatus(order.order_id, status)} 
                              disabled={order.status === status}
                              style={{
                                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                cursor: order.status === status ? 'default' : 'pointer',
                                border: '1px solid', borderColor: info.color,
                                background: order.status === status ? info.bg : 'white',
                                color: info.color, opacity: order.status === status ? 1 : 0.7,
                              }}>
                              {info.label}
                              {status === 'confirmed' && order.status !== 'confirmed' && ' 📧'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Refresh + auto-refresh indicator */}
        {restaurant && (
          <div style={{ marginTop: 20, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <button className="btn btn-outline" onClick={() => loadOrders(restaurant.restaurant_id, false)}>
              🔄 Refresh Orders
            </button>
            <span style={{ fontSize: 12, color: '#999' }}>⏱️ Auto-refreshes every 15 seconds</span>
          </div>
        )}
      </div>

      {/* Confirmation Time Popup */}
      {confirmingOrder && (
        <ConfirmTimePopup
          order={confirmingOrder}
          onConfirm={handleConfirmWithTime}
          onCancel={() => setConfirmingOrder(null)}
        />
      )}
    </AdminLayout>
  );
}